import { Component, OnInit, OnDestroy, Inject, ChangeDetectorRef, ViewChild } from "@angular/core";
import { ProfileService } from "../providers/profile.service";
import { TransConfigService } from "../providers/trans-config.service";
import { BvDialogService, BvDialogRef, BV_DIALOG_DATA } from "../design-system";
import { Observable } from "rxjs";
import { Subscription } from "rxjs";
import { TranslateService, TranslateModule } from "@ngx-translate/core";
import { RoutingService } from "../providers/routing.service";
import { AlertDialogComponent } from "../shared/alert-dialog/alert-dialog.component";
import { ConfirmDialogComponent } from "../shared/confirm-dialog/confirm-dialog.component";
import { AuthService } from "../providers/auth.service";
import { SubscriptionService } from "../providers/subscription.service";
import { ConfigService } from "../providers/config.service";
import { ApiService } from "../providers/api.service";
import { PaperSizeService } from "../providers/paper-size.service";
import * as math from "mathjs/dist/math";
import { PayInvoiceComponent } from "../subscription/pay-invoice/pay-invoice.component";
import { ResetEmailComponent } from "./reset-email/reset-email.component";
import { DeleteAccountComponent } from "./delete-account/delete-account.component";
import { LoadingOverlayComponent } from "../shared/loading-overlay/loading-overlay.component";
import { BvButtonComponent } from "../design-system/primitives/button.component";
import { BvIconComponent } from "../design-system/primitives/icon.component";
import { NgClass, CurrencyPipe, DatePipe, DecimalPipe } from "@angular/common";
import { FormsModule, NgForm } from "@angular/forms";
import { BvTooltipDirective } from "../design-system/primitives/tooltip.directive";
import { BvInputDirective } from "../design-system/primitives/input.directive";
import { PhotoPickerComponent } from "../shared/photo-picker/photo-picker.component";
import { BvRadioGroupComponent, BvRadioComponent } from "../design-system/primitives/radio.component";
import { BvSwitchComponent } from "../design-system/primitives/switch.component";
import { ObjArrPipe } from "../shared/obj-arr.pipe";
import { ISO_COUNTRY_CODES } from "../shared/iso-country-codes";

export enum ProfileTabs {
  profile,
  language,
  subscription,
  payment_methods,
  preferences,
  danger_zone,
}

@Component({
    selector: "app-profile",
    templateUrl: "./profile.component.html",
    styleUrls: ["./profile.component.scss"],
    imports: [LoadingOverlayComponent, BvButtonComponent, BvIconComponent, FormsModule, BvTooltipDirective, BvInputDirective, PhotoPickerComponent, BvRadioGroupComponent, BvRadioComponent, TranslateModule, NgClass, BvSwitchComponent, CurrencyPipe, DatePipe, DecimalPipe, ObjArrPipe]
})
export class ProfileComponent implements OnInit, OnDestroy {
  // Profile tab
  displayName: string = "";
  firstName: string = "";
  lastName: string = "";
  email: string = "";
  imgType!: string;
  provider!: string;
  imgURL!: string;
  newImg!: string;
  uploading: boolean = false;
  originalImgType!: string;
  profTab: number = 0;
  // Subscription tab: storage the account owns, loaded when the tab opens
  // (null until then, or when the callable fails).
  storageBytes: number | null = null;
  showAddCard: boolean = false;
  profSub!: Subscription;
  // The #profForm template ref lives inside @if (profTab === 0), out of scope
  // for the footer button's [disabled] binding — without this ViewChild the
  // validity guard silently resolved to undefined and never disabled anything.
  @ViewChild("profForm") profForm?: NgForm;

  // Language tab
  languageList!: Observable<any>;
  language!: string;
  // Regional format: the user's chosen region drives date/number formatting,
  // independent of the UI language. "" = Auto (platform default).
  region: string = "";
  regionOptions: { code: string; name: string; label: string }[] = [];
  // Suffix shown on the "Auto (detected)" option, e.g. " — France (31/01/2026)",
  // so the user can see which region auto-detect resolves to (their geo country).
  autoRegionHint: string = "";

  constructor(
    public profServ: ProfileService,
    public transConfigServ: TransConfigService,
    private translate: TranslateService,
    private dialog: BvDialogService,
    public dialogRef: BvDialogRef<ProfileComponent>,
    public routing: RoutingService,
    public auth: AuthService,
    public subServ: SubscriptionService,
    public configServ: ConfigService,
    private api: ApiService,
    public paperSize: PaperSizeService,

    @Inject(BV_DIALOG_DATA) public data: any,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit() {
    this.displayName = this.profServ.latestProfile.dispName;
    this.firstName = this.profServ.latestProfile.firstName || "";
    this.lastName = this.profServ.latestProfile.lastName || "";
    this.email = this.auth.userInfo.email;
    this.imgURL = this.profServ.latestProfile.imgURL;
    this.imgType = this.profServ.latestProfile.imgType;
    if (!this.imgType) this.imgType = this.profServ.latestProfile.provider;
    this.provider = this.profServ.latestProfile.provider;
    this.originalImgType = this.profServ.latestProfile.imgType;
    this.language = this.profServ.latestProfile.selLang || "en";
    this.region = this.profServ.latestProfile.region || "";
    this.buildRegionOptions();
    if (this.data) this.selectTab(this.data.tab);
  }
  // Build the regional-format picker: every ISO country, labelled with its
  // localized name plus a sample short date so the format ordering is obvious
  // (e.g. "France (31/01/2026)" vs "United States (1/31/2026)").
  private buildRegionOptions() {
    const uiLang = (
      this.profServ.latestProfile.selLang ||
      this.translate.currentLang ||
      "en"
    ).split("-")[0];
    let names: Intl.DisplayNames | undefined;
    try {
      names = new Intl.DisplayNames([uiLang], { type: "region" });
    } catch {}
    const sample = new Date(2026, 0, 31); // 31 Jan disambiguates D/M ordering
    const formatFor = (code: string): string => {
      try {
        return new Intl.DateTimeFormat(`${uiLang}-${code}`, {
          dateStyle: "short",
        }).format(sample);
      } catch {
        return "";
      }
    };
    const nameOf = (code: string): string => {
      try {
        return names?.of(code) || code;
      } catch {
        return code;
      }
    };
    this.regionOptions = ISO_COUNTRY_CODES.map((code) => {
      const name = nameOf(code);
      const example = formatFor(code);
      return { code, name, label: example ? `${name} (${example})` : name };
    }).sort((a, b) => a.name.localeCompare(b.name, uiLang));

    const geo = (
      this.profServ.latestProfile.locationData?.countryCode || ""
    ).toUpperCase();
    if (geo) {
      const example = formatFor(geo);
      this.autoRegionHint = example
        ? ` — ${nameOf(geo)} (${example})`
        : ` — ${nameOf(geo)}`;
    }
  }
  initCard() {
    if (!this.subServ.card) this.subServ.initStripe();
  }
  mountCard() {
    this.showAddCard = !this.showAddCard;
    if (this.showAddCard) {
      if (!this.subServ.card) this.subServ.initStripe();
      setTimeout(() => {
        this.subServ.card.mount("#add-card-element");
      });
    }
  }
  selectTab(tab: number) {
    this.profTab = tab;
    if (tab == 2) this.loadStorage();
    if (tab == 3) this.initCard();
  }
  loadStorage() {
    this.api.getAccountStorage().then((r) => {
      this.storageBytes = r ? r.bytes : null;
      this.cdr.markForCheck();
    });
  }
  /** Storage used as a number + unit (MB below 1 GB). */
  get storageUsed(): { value: number; unit: string; digits: string } | null {
    if (this.storageBytes === null) return null;
    const mb = this.storageBytes / 1048576;
    return mb < 1024
      ? { value: mb, unit: "MB", digits: mb < 10 ? "1.0-1" : "1.0-0" }
      : { value: mb / 1024, unit: "GB", digits: "1.1-1" };
  }
  tabChanged(evt: any) {
    if (evt.index == 3) this.initCard();
  }
  saveProfile() {
    this.uploading = true;
    if (
      this.newImg &&
      (this.imgType === "custom" || this.provider == "password")
    ) {
      this.profServ.uploadProfileImage(
        this.newImg,
        (newImgURL, newImgType) => {
          this.uploading = false; this.cdr.markForCheck();
          this.profServ.updateUser(
            this.displayName,
            newImgURL,
            newImgType,
            this.language,
            this.firstName,
            this.lastName,
            this.region
          );
          this.dialog.closeAll();
        },
        (error) => {
          console.log(error);
          this.translate
            .get(["FILE_UPLOAD_FAILED", "CLOSE"])
            .subscribe((res: any) => {
              this.dialog.open(AlertDialogComponent, {
                data: {
                  message: res["FILE_UPLOAD_FAILED"],
                  closeButton: res["CLOSE"],
                },
                panelClass: "alert-dialog-container",
              });
            });
          this.uploading = false; this.cdr.markForCheck();
        }
      );
    } else {
      // When custom image is selected but no new image is uploaded, the type should still be custom.
      let newImgType =
        this.imgType == "custom" ? this.originalImgType : this.imgType;
      this.profServ.updateUser(
        this.displayName,
        this.imgURL,
        newImgType,
        this.language,
        this.firstName,
        this.lastName,
        this.region
      );
      this.dialog.closeAll();
    }
  }
  profImageSelect(newType: string) {
    if (newType == this.provider) {
      this.imgURL = this.profServ.getImageUrlForConnectedUser();
    }
  }
  setCroppedImg(evt: any) {
    this.newImg = evt.base64;
  }
  changePlan() {
    this.routing.toSubscription();
    this.dialog.closeAll();
  }
  getActivePlanName() {
    if (this.profServ.latestSubscription)
      return this.configServ.getPlanName(this.profServ.latestSubscription.code);
    else return this.configServ.getPlanName();
  }
  getSubscriptionPrice(): string {
    const sub = this.profServ.latestSubscription;
    if (!sub || !sub.plan) return "";
    const currency = (sub.plan.currency || "usd").toString().toUpperCase();
    const ZERO_DECIMAL = new Set(["BIF","CLP","DJF","GNF","JPY","KMF","KRW","MGA","PYG","RWF","UGX","VND","VUV","XAF","XOF","XPF"]);
    const rawAmount = sub.plan.amount || 0;
    const amount = ZERO_DECIMAL.has(currency) ? rawAmount : rawAmount / 100;
    let formatted: string;
    try {
      formatted = new Intl.NumberFormat("en", {
        style: "currency",
        currency,
        currencyDisplay: "symbol",
        minimumFractionDigits: ZERO_DECIMAL.has(currency) ? 0 : 2,
        maximumFractionDigits: ZERO_DECIMAL.has(currency) ? 0 : 2,
      }).format(amount);
    } catch (e) {
      formatted = `${currency} ${amount.toFixed(2)}`;
    }
    const intervalLabel = sub.plan.interval === "year" ? " / year" : " / month";
    return `${formatted}${intervalLabel}`;
  }
  payInvoice() {
    if (this.routing.getPath() != "subscription") {
      this.subServ.openPayInv = true;
      this.routing.toSubscription();
      this.dialog.closeAll();
    } else this.dialog.open(PayInvoiceComponent);
  }
  ngOnDestroy() {
    if (this.profSub) this.profSub.unsubscribe();
  }
  addCard() {
    this.subServ.addCard(this.profServ.uid!);
  }
  deleteCard(card: any) {
    this.subServ.deleteCard(card.id);
  }
  setDefaultCard(card: any) {
    this.subServ.changeDefaultCard(card.id);
  }
  getAcctBalance() {
    if (
      this.profServ.latestProfile &&
      this.profServ.latestProfile.stripeAcctBalance
    )
      return math.round(
        this.profServ.latestProfile.stripeAcctBalance / -100,
        2
      );
    else return 0;
  }
  getAmountDue() {
    if (this.profServ.latestProfile && this.profServ.latestProfile.amount_due)
      return math.round(this.profServ.latestProfile.amount_due / 100, 2);
    else return 0;
  }

  openResetEmailModal() {
    this.dialog.closeAll();
    this.dialog.open(ResetEmailComponent, { data: this.email });
  }

  openDeleteAccount() {
    this.dialog.closeAll();
    this.dialog.open(DeleteAccountComponent);
  }

  openDeletePhotoConfirm() {
    this.translate
      .get([
        "PROFILE_IMAGE",
        "CONFIRM_DELETE",
        "CONFIRM",
        "CANCEL",
      ])
      .subscribe((res: any) => {
        const dialogRef = this.dialog.open(ConfirmDialogComponent, {
          data: {
            title: res["CONFIRM_DELETE"],
            message: res["PROFILE_IMAGE"],
            acceptButton: res["CONFIRM"],
            cancelButton: res["CANCEL"],
          },
          panelClass: "confirm-dialog-container",
        });

        dialogRef.afterClosed().subscribe((confirmed) => {
          if (confirmed) {
            this.deleteProfilePicture();
          }
        });
      });
  }

  deleteProfilePicture() {
    this.uploading = true;
    this.profServ
      .deleteProfilePicture()
      .then((result) => {
        this.uploading = false; this.cdr.markForCheck();
        this.imgURL = result.imgURL;
        this.dialog.closeAll();
      })
      .catch((error) => {
        this.uploading = false; this.cdr.markForCheck();
        this.translate
          .get(["DELETE_FAILED", "CLOSE"])
          .subscribe((res: any) => {
            this.dialog.open(AlertDialogComponent, {
              data: {
                message: res["DELETE_FAILED"],
                closeButton: res["CLOSE"],
              },
              panelClass: "alert-dialog-container",
            });
          });
      });
  }
}
